import { Product } from "../../../domain/entities/product/Product";
import { ProductPreview } from "../../../domain/projections/ProductPreview";
import { VariantWithColorAndImage } from "../../../domain/projections/VariantWithColorAndImage";
import { ProductRepository } from "../../../domain/repositories/ProductRepository";
import { createHttpClients } from "../shared/createHttpClients";
import {
  BenefitsContentDTO,
  mapBenefitsContentDtoToBenefitsMediaUrl,
  mapCrossselingProductsDtoToProductsPreview,
  mapProductDetailsDTOAndSizesDTOToProduct,
  mapSizeVariantsDtoToSizes,
  mapSizeVariantsDtoToVariantWithColorAndImages,
  ProductDetailsDTO,
  ProductPreviewDTO,
  SizeVariantsDTO,
} from "./responseMapper";

class ProductRepositoryImpl implements ProductRepository {
  private static instance: ProductRepositoryImpl;

  private constructor() {}

  public static getInstance(): ProductRepositoryImpl {
    if (!ProductRepositoryImpl.instance) {
      ProductRepositoryImpl.instance = new ProductRepositoryImpl();
    }

    return ProductRepositoryImpl.instance;
  }

  public async getProductDetails(
    variantId: string,
    isRecommended: boolean,
    auth: string,
    apiUrl: () => string,
  ): Promise<Product> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const dataPromises: [Promise<ProductDetailsDTO>, Promise<SizeVariantsDTO>] = [
        httpGet<ProductDetailsDTO>({
          endpoint: `/direct-buy/v1/product-details?variantId=${variantId}`,
        }),
        httpGet<SizeVariantsDTO>({
          endpoint: `/direct-buy/v1/size-variants?variantIds=${variantId}&isRecommended=${isRecommended}`,
        }),
      ];

      const [productDetailsDTO, sizesDTO] = await Promise.all(dataPromises);
      const sizes = mapSizeVariantsDtoToSizes(sizesDTO);

      return mapProductDetailsDTOAndSizesDTOToProduct(
        productDetailsDTO,
        sizes,
        variantId,
        sizesDTO.sizes[0]?.sizeVariant,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getColorVariants(
    variantId: string[],
    auth: string,
    apiUrl: () => string,
  ): Promise<VariantWithColorAndImage[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const response = await httpGet<SizeVariantsDTO>({
        endpoint: `/direct-buy/v1/size-variants?variantIds=${variantId}`,
      });
      return mapSizeVariantsDtoToVariantWithColorAndImages(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getTopSelling(auth: string, apiUrl: () => string): Promise<ProductPreview[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const response = await httpGet<{ productVariants: ProductPreviewDTO[] }>({
        endpoint: `/direct-buy/v1/top-selling-products`,
      });
      return mapCrossselingProductsDtoToProductsPreview(response.productVariants);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  public async getLookAndLikeProducts(auth: string, apiUrl: () => string): Promise<ProductPreview[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const response = await httpGet<{ productVariants: ProductPreviewDTO[] }>({
        endpoint: `/direct-buy/v1/look-and-like-products`,
      });
      return mapCrossselingProductsDtoToProductsPreview(response.productVariants);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  public async getSimilarProducts(auth: string, variantId: string, apiUrl: () => string): Promise<ProductPreview[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const response = await httpGet<{ productVariants: ProductPreviewDTO[] }>({
        endpoint: `/direct-buy/v1/similar-products?variantId=${variantId}`,
      });
      return mapCrossselingProductsDtoToProductsPreview(response.productVariants);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  public async getOtherLookProducts(
    lookId: string,
    variantId: string,
    auth: string,
    apiUrl: () => string,
  ): Promise<ProductPreview[]> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const response = await httpGet<{ productVariants: ProductPreviewDTO[] }>({
        endpoint: `/direct-buy/v1/other-look-products?lookId=${lookId}&viewedProductVariantId=${variantId}`,
      });
      return mapCrossselingProductsDtoToProductsPreview(response.productVariants);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  public async addToStockNotification(auth: string, variantId: string, apiUrl: () => string): Promise<void> {
    try {
      const { httpPost } = createHttpClients(auth, apiUrl);
      await httpPost({
        endpoint: `/direct-buy/v1/notify-stock`,
        body: { variantId },
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  public async getBenefitsMediaUrl(variantId: string, auth: string, apiUrl: () => string): Promise<string> {
    try {
      const { httpGet } = createHttpClients(auth, apiUrl);
      const response = await httpGet<BenefitsContentDTO>({
        endpoint: `/direct-buy/v1/benefits-content/${variantId}`,
      });

      return mapBenefitsContentDtoToBenefitsMediaUrl(response);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
}

export { ProductRepositoryImpl };
